// Translations of the PUBLIC website (lib/siteLanguages.js), one file set per
// language under src/locales/site/<lang>/ (see docs/I18N.md).
//
// The Spanish text is the page code itself (src/content/…), so Spanish needs
// no file and costs nothing extra. Every other language loads as its own
// chunk, only when someone visits it.
//
// Each language gets its own i18next instance (a clone sharing the app's
// translations), so a public page can read in French while the APP stays in
// the language its user chose: visiting /fr/ never touches the app's language
// or what it saved.
import i18n, { ensureLanguage } from './i18n';
import { reloadOnceForStaleChunk } from './lazyWithReload';
import { CODES, DEFAULT_LANGUAGE, baseCode, isSiteLanguage } from './siteLanguages';

/* eslint-disable max-len */
const BUNDLES = {
  ca: () => import(/* webpackChunkName: "site-ca" */ '../locales/site/ca'),
  en: () => import(/* webpackChunkName: "site-en" */ '../locales/site/en'),
  bg: () => import(/* webpackChunkName: "site-bg" */ '../locales/site/bg'),
  cs: () => import(/* webpackChunkName: "site-cs" */ '../locales/site/cs'),
  da: () => import(/* webpackChunkName: "site-da" */ '../locales/site/da'),
  de: () => import(/* webpackChunkName: "site-de" */ '../locales/site/de'),
  et: () => import(/* webpackChunkName: "site-et" */ '../locales/site/et'),
  el: () => import(/* webpackChunkName: "site-el" */ '../locales/site/el'),
  fr: () => import(/* webpackChunkName: "site-fr" */ '../locales/site/fr'),
  ga: () => import(/* webpackChunkName: "site-ga" */ '../locales/site/ga'),
  hr: () => import(/* webpackChunkName: "site-hr" */ '../locales/site/hr'),
  it: () => import(/* webpackChunkName: "site-it" */ '../locales/site/it'),
  lv: () => import(/* webpackChunkName: "site-lv" */ '../locales/site/lv'),
  lt: () => import(/* webpackChunkName: "site-lt" */ '../locales/site/lt'),
  hu: () => import(/* webpackChunkName: "site-hu" */ '../locales/site/hu'),
  mt: () => import(/* webpackChunkName: "site-mt" */ '../locales/site/mt'),
  nl: () => import(/* webpackChunkName: "site-nl" */ '../locales/site/nl'),
  pl: () => import(/* webpackChunkName: "site-pl" */ '../locales/site/pl'),
  pt: () => import(/* webpackChunkName: "site-pt" */ '../locales/site/pt'),
  ro: () => import(/* webpackChunkName: "site-ro" */ '../locales/site/ro'),
  sk: () => import(/* webpackChunkName: "site-sk" */ '../locales/site/sk'),
  sl: () => import(/* webpackChunkName: "site-sl" */ '../locales/site/sl'),
  fi: () => import(/* webpackChunkName: "site-fi" */ '../locales/site/fi'),
  sv: () => import(/* webpackChunkName: "site-sv" */ '../locales/site/sv'),
};
/* eslint-enable max-len */

// { [lang]: { app, groups: { chrome, landing, … }, pages: { tpv, … }, seo } }
const DATA = { [DEFAULT_LANGUAGE]: {} };
const inflight = {};
const listeners = new Set();

function store(lang, mod) {
  const data = (mod && (mod.default || mod)) || {};
  // Keys of the app's own translations that the public pages show (the
  // landing's texts, the plan cards…), for languages the app is not in.
  if (data.app) i18n.addResourceBundle(lang, 'translation', data.app, true, false);
  DATA[lang] = data;
  listeners.forEach((fn) => fn());
}

export function isSiteLanguageLoaded(lang) {
  return !!DATA[baseCode(lang)];
}

// Loads a language's website texts (once). English and Catalan also need the
// app's own file, where the landing's texts already were.
export function ensureSiteLanguage(lng) {
  const lang = baseCode(lng);
  if (!isSiteLanguage(lang) || DATA[lang]) return Promise.resolve();
  if (!inflight[lang]) {
    inflight[lang] = Promise.all([BUNDLES[lang](), ensureLanguage(lang)])
      .then(([mod]) => store(lang, mod))
      .catch((err) => {
        delete inflight[lang];
        // A deploy renamed the chunk under an open tab: reload once.
        if (reloadOnceForStaleChunk(err)) return new Promise(() => {});
        throw err;
      });
  }
  return inflight[lang];
}

export function subscribeSiteLanguages(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// The translation of one text group (content/siteText.js), or undefined.
export function siteGroup(lang, group) {
  return DATA[baseCode(lang)]?.groups?.[group];
}

// The translation of one content page (src/content/pages/<id>.js), or undefined.
export function sitePage(lang, id) {
  return DATA[baseCode(lang)]?.pages?.[id];
}

// Title and description of one public page, or undefined.
export function siteSeo(lang, path) {
  return DATA[baseCode(lang)]?.seo?.pages?.[path];
}

// While a clone starts it must not save its language as the app's.
let silent = false;
const detector = i18n.services.languageDetector;
if (detector && !detector.siteGuard) {
  const save = detector.cacheUserLanguage.bind(detector);
  detector.cacheUserLanguage = (...args) => { if (!silent) save(...args); };
  detector.siteGuard = true;
}

const instances = {};

// The i18next instance a public page in `lang` renders with.
export function siteInstance(lng) {
  const lang = isSiteLanguage(lng) ? baseCode(lng) : DEFAULT_LANGUAGE;
  if (!instances[lang]) {
    silent = true;
    try {
      instances[lang] = i18n.cloneInstance({ lng: lang });
    } finally {
      silent = false;
    }
    // Never saves anything, ever after.
    instances[lang].services = { ...instances[lang].services, languageDetector: undefined };
  }
  return instances[lang];
}

// In tests every language is in place from the start (as lib/i18n.js does).
if (process.env.NODE_ENV === 'test') {
  /* eslint-disable global-require */
  for (const lang of CODES) {
    if (lang !== DEFAULT_LANGUAGE) store(lang, require(`../locales/site/${lang}`));
  }
  /* eslint-enable global-require */
}
